import React from 'react';
import Divider from '@mui/material/Divider';
import DevicesIcon from '@mui/icons-material/StorageRounded';
import PeopleIcon from '@mui/icons-material/PeopleRounded';
import PersonIcon from '@mui/icons-material/PersonRounded';
import ProjectsIcon from '@mui/icons-material/DashboardRounded';
import GridOnRounded from '@mui/icons-material/GridOnRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import ImportContactsRoundedIcon from '@mui/icons-material/ImportContactsRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { whitelabel } from '#config/whitelabel';
import { webPortalApi } from '#config/web-portal-api';
import { getRequestOptions } from '#helper';

const trailPagesList = [
  { label: 'Shipment', route: '/shipment-log', moduleId: [1, 3] },
  { label: 'Sites', route: '/warehouse-log', moduleId: [2, 3] },
  { label: 'Devices', route: '/device-log', divider: <Divider /> },
  { label: 'Projects', route: '/project-log', adminOnly: true },
  { label: 'Users', route: '/user-log', adminOnly: true }
];

const getFreezerUrls = () => ({
  whitelist: import.meta.env?.VITE_FREEZER_LINK_WHITE_LIST ?? '',
  url: import.meta.env?.VITE_FREEZER_LINK_URL ?? ''
});

const getAvailablePagesListByModules = (pageList, userRootProject = {}) => {
  const modulesIds = userRootProject?.modules?.map(pm => pm.id);

  if (!modulesIds?.length) {
    return pageList;
  }

  return pageList.filter(page => {
    if (!page.moduleId) {
      return true;
    }

    const { moduleId } = page;
    const foundedModules = moduleId.filter(pm => modulesIds.includes(pm));

    return foundedModules.length === moduleId.length;
  });
};

const getTrailPagesList = (userType = 10, userRootProject) => {
  const trailPages = trailPagesList.filter(p => userType < 10 || (userType >= 10 && !p.adminOnly));
  return getAvailablePagesListByModules(trailPages, userRootProject);
};

const includeFreezerPage = () => {
  const { whitelist, url } = getFreezerUrls();
  const whitelabelName = whitelabel.getName();

  return whitelist?.includes(whitelabelName) ?
    {
      label: 'Freezers',
      route: url,
      icon: <AcUnitIcon />,
      externalRedirect: true,
      onClickHandler: async e => {
        try {
          e.preventDefault();

          const response = await fetch(
            webPortalApi.buildApiUrl('user/application-code'),
            {
              ...getRequestOptions(),
              method: 'GET'
            }
          );

          const data = await response.json();

          window.open(`${url}?code=${data.code}`, '_blank');
        } catch (error) {
          console.error(error);
        }
      }
    } :
    null;
};

const getDefaultPageList = (userType = 10, id = 1, userRootProject) => ([
  {
    label: 'Dashboard',
    route: '/dashboard',
    icon: <PieChartRoundedIcon />
  },
  {
    label: 'Shipment',
    route: '/shipment',
    icon: <MapRoundedIcon />,
    moduleId: [1]
  },
  {
    label: 'Sites',
    route: '/warehouse',
    icon: <GridOnRounded />,
    moduleId: [2]
  },
  includeFreezerPage(),
  {
    label: 'Audit trail',
    icon: <SpeakerNotesOutlinedIcon />,
    subMenuList: getTrailPagesList(userType, userRootProject),
    moduleId: [3]
  },
  {
    label: 'Project setup',
    route: '/project',
    icon: <ProjectsIcon />,
    adminOnly: true,
    divider: <Divider />
  },
  {
    label: 'Devices',
    route: '/device',
    icon: <DevicesIcon />
  },
  {
    label: 'Users',
    route: '/user',
    icon: <PeopleIcon />,
    adminOnly: true
  },
  {
    label: 'Address book',
    route: '/addressbook',
    icon: <ImportContactsRoundedIcon />,
    adminOnly: true,
    moduleId: [4]
  },
  {
    label: 'Profile',
    route: `/profile/${id}`,
    icon: <PersonIcon />,
    divider: <Divider />
  }
]).filter(p => p !== null);

export const getPagesList = (userType = 10, id = 1, userRootProject) => {
  const defaultPagesList = getDefaultPageList(userType, id, userRootProject);
  let pagesList = defaultPagesList.filter(p => userType < 10 || (userType >= 10 && !p.adminOnly));

  if (userRootProject) {
    pagesList = getAvailablePagesListByModules(pagesList, userRootProject);
  }

  return pagesList;
};

export const extendedPageList = (userType = 10, id = 1, userRootProject) => {
  const defaultPageList = getDefaultPageList(userType, id);
  const allPagesList = [
    { label: 'Add Shipment', route: '/addshipment' },
    { label: 'Device Overview', route: '/device/' },
    { label: 'Add Project', route: '/addproject', adminOnly: true },
    { label: 'Add Users', route: '/adduser', adminOnly: true },
    { label: 'Create Address book', route: '/addressbook/create', adminOnly: true },
    { label: 'Site Manager', route: '/warehouse-manager' },
    { label: 'Site Create', route: '/create-warehouse' },
    { label: 'Export', route: '/export/' },
    { label: 'Reports', route: '/reports' },
    { label: 'Application Excursion Report', route: '/reports/application-excursion' },
    ...defaultPageList,
    ...trailPagesList
  ];

  let pageList = allPagesList.filter(p => userType < 10 || (userType >= 10 && !p.adminOnly));

  if (userRootProject) {
    pageList = getAvailablePagesListByModules(pageList, userRootProject);
  }

  return pageList;
};
